/**
 * @author      carlos.soto@zumper.com (Carlos J. Soto)
 * @copyright   Copyright (c) 2024, Zumper
 * @description User Menu shared components
 */

import { ChevronRight } from '@zcode-ui/icons'
import { MenuGroup, MenuItem } from '@zcode-ui/shadz-ui'
import { selectIsBot } from '@zcode-ui/web-zumper-global-state'
import { LinkOrButton, type LinkOrButtonProps } from '@zcode-ui/web-zumper-ui'
import * as React from 'react'
import { useSelector } from 'react-redux'

interface MenuButtonProps extends React.ComponentProps<typeof MenuItem> {
  closeOnSelect?: boolean
  type?: 'button' | 'reset' | 'submit'
  withIcon?: boolean
}

export const UserMenuBtnRow = ({
  children,
  closeOnSelect,
  type = 'button',
  withIcon = true,
  ...props
}: MenuButtonProps) => {
  const isBot = useSelector(selectIsBot)
  if (isBot) {
    return (
      <button type={type}>
        {children}
        {withIcon && <ChevronRight fill="var(--chakra-colors-gray-600)" size={10} />}
      </button>
    )
  }

  return (
    <MenuItem
      asChild
      className="text-gray-950 text-sm justify-between leading-[150%] px-4 w-full [&_svg]:size-[unset] focus:bg-transparent focus-visible:bg-gray-50 hover:bg-gray-50 hover:cursor-pointer"
      onSelect={(e) => {
        if (!closeOnSelect) {
          e.preventDefault()
        }
      }}
      {...props}
    >
      <button
        type={type}
        onClick={(e) => {
          if (!closeOnSelect) {
            e.preventDefault()
          }
        }}
      >
        {children}
        {withIcon && <ChevronRight fill="var(--chakra-colors-gray-600)" size={10} />}
      </button>
    </MenuItem>
  )
}

type LinkRowProps =
  | (Pick<LinkOrButtonProps, 'children' | 'href' | 'onClick'> & { href: string })
  | (Pick<LinkOrButtonProps, 'children' | 'to' | 'onClick'> & {
      to: NonNullable<LinkOrButtonProps['to']>
    })

export const UserMenuLinkRow = (props: LinkRowProps) => (
  <LinkOrButton
    className="flex h-8 hover:bg-gray-50 items-center px-4 text-sm w-full"
    role="menuitem"
    {...props}
  />
)

export interface MenuSectionProps {
  heading?: string
  children: React.ReactNode
}

export const MenuSection = ({ heading, children }: MenuSectionProps) => {
  const isBot = useSelector(selectIsBot)
  if (isBot) {
    return (
      <div role="group">
        <div className="flex items-start border-b border-solid border-gray-150 flex-col gap-3 mb-2 pb-4 pt-2 w-full">
          {heading && (
            <p className="text-gray-600 text-[9px] font-bold tracking-[1.5px] leading-3 px-4 uppercase">
              {heading}
            </p>
          )}
          <div className="m-0 items-start w-full flex-col">{children}</div>
        </div>
      </div>
    )
  }

  return (
    <MenuGroup>
      <div className="flex items-start border-b border-solid border-gray-150 flex-col gap-3 mb-2 pb-4 pt-2 w-full">
        {heading && (
          <p className="text-gray-600 text-[9px] font-bold tracking-[1.5px] leading-3 px-4 uppercase">
            {heading}
          </p>
        )}
        <div className="m-0 items-start w-full flex-col">{children}</div>
      </div>
    </MenuGroup>
  )
}
